import React, { useEffect, useMemo, useState } from "react";
import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion";
import { Home as HomeIcon, Inbox, CheckCheck, TrendingUp, Clock, Gauge, Radio } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/api";

function useAnimatedNumber(target, opts = {}) {
    const { duration = 1.5, decimals = 0, ready = true } = opts;
    const mv = useMotionValue(0);
    const rounded = useTransform(mv, (v) => {
        const factor = 10 ** decimals;
        return (Math.round(v * factor) / factor).toLocaleString("fr-FR", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    });
    useEffect(() => {
        if (!ready) return;
        const controls = animate(mv, Number(target) || 0, { duration, ease: [0.16, 1, 0.3, 1] });
        return () => controls.stop();
    }, [target, ready, duration, mv]);
    return rounded;
}

export default function PublicCockpit() {
    const { t } = useI18n();
    const [stats, setStats] = useState(null);
    const ref = useMemo(() => React.createRef(), []);
    const inViewFlag = useInView(ref, { once: true, margin: "-80px" });

    useEffect(() => {
        let alive = true;
        api.get("/stats/public")
            .then((r) => alive && setStats(r.data))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    return (
        <section
            ref={ref}
            className="relative bg-brand-navy text-white overflow-hidden"
            data-testid="public-cockpit"
        >
            <div className="absolute inset-0 opacity-20 bg-grain pointer-events-none" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
                <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-3 text-brand-gold text-[0.7rem] tracking-[0.3em] uppercase font-semibold mb-4">
                            <Radio className="w-3.5 h-3.5 animate-pulse" />
                            {t("public_cockpit.live")} · {t("public_cockpit.kicker")}
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
                            {t("public_cockpit.title")}
                        </h2>
                    </div>
                    <p className="lg:col-span-4 text-white/70 max-w-md text-base leading-relaxed">
                        {t("public_cockpit.subtitle")}
                    </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="public-cockpit-grid">
                    <Card
                        icon={Gauge}
                        label={t("public_cockpit.performance")}
                        value={stats?.performance ?? 0}
                        suffix="%"
                        highlight
                        inView={inViewFlag}
                        span={2}
                        decimals={1}
                        testid="pub-performance"
                    />
                    <Card
                        icon={CheckCheck}
                        label={t("public_cockpit.response_rate")}
                        value={stats?.response_rate ?? 0}
                        suffix="%"
                        inView={inViewFlag}
                        decimals={1}
                        testid="pub-response-rate"
                    />
                    <Card
                        icon={TrendingUp}
                        label={t("public_cockpit.processing_rate")}
                        value={stats?.processing_rate ?? 0}
                        suffix="%"
                        inView={inViewFlag}
                        decimals={1}
                        testid="pub-processing-rate"
                    />
                    <Card
                        icon={Clock}
                        label={t("public_cockpit.avg_response")}
                        value={stats?.avg_response_hours ?? 0}
                        suffix="h"
                        inView={inViewFlag}
                        decimals={1}
                        testid="pub-avg-response"
                    />
                    <Card
                        icon={HomeIcon}
                        label={t("public_cockpit.properties")}
                        value={stats?.properties_count ?? 0}
                        inView={inViewFlag}
                        testid="pub-properties"
                    />
                    <Card
                        icon={Inbox}
                        label={t("public_cockpit.inquiries")}
                        value={stats?.inquiries_total ?? 0}
                        inView={inViewFlag}
                        span={2}
                        testid="pub-inquiries"
                    />
                </div>

                <p className="mt-8 text-xs text-white/45 max-w-2xl">{t("public_cockpit.note")}</p>
            </div>
        </section>
    );
}

function Card({ icon: Icon, label, value, suffix = "", highlight, inView, span, decimals = 0, testid }) {
    const anim = useAnimatedNumber(value, { decimals, ready: inView });
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid={testid}
            className={`${span === 2 ? "col-span-2" : ""} ${
                highlight
                    ? "bg-brand-gold text-brand-navy"
                    : "bg-white/[0.04] border border-white/10 text-white"
            } p-5 sm:p-6 relative overflow-hidden group`}
        >
            <div className="flex items-start justify-between mb-4">
                <div
                    className={`w-9 h-9 flex items-center justify-center ${
                        highlight ? "bg-brand-navy/10 text-brand-navy" : "bg-brand-gold/15 text-brand-gold"
                    }`}
                >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                </div>
            </div>
            <div className="font-display text-4xl sm:text-5xl leading-none tracking-tight">
                <motion.span>{anim}</motion.span>
                {suffix ? <span className="text-2xl align-top ml-1 opacity-70">{suffix}</span> : null}
            </div>
            <div
                className={`mt-3 text-[0.6rem] sm:text-[0.65rem] tracking-[0.22em] uppercase font-bold ${
                    highlight ? "text-brand-navy/80" : "text-white/60"
                }`}
            >
                {label}
            </div>
        </motion.div>
    );
}
